/**
 * useOrgSettings hook
 *
 * Fetches organization details and handles settings update mutation.
 * Used by the OrgSettingsClient component.
 */

"use client";

import { useState, useCallback } from "react";
import { useApiQuery } from "@/hooks/useApiQuery";
import type { Organization, UpdateOrganization } from "@/lib/organizations/types";

export function useOrgSettings(_orgId: string) {
  const {
    data: organization,
    isLoading,
    error: fetchError,
    refresh,
  } = useApiQuery<Organization>("/api/tenant/update", {
    errorMessage: "Einstellungen konnten nicht geladen werden",
  });

  const [isSaving, setIsSaving] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const updateSettings = useCallback(
    async (data: UpdateOrganization): Promise<boolean> => {
      setIsSaving(true);
      setMutationError(null);
      setSaveSuccess(false);

      try {
        const res = await fetch("/api/tenant/update", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });

        if (!res.ok) {
          const json = await res.json().catch(() => ({}));
          throw new Error(json.error || "Einstellungen konnten nicht gespeichert werden");
        }

        // Refresh to get the updated organization
        refresh();
        setSaveSuccess(true);
        return true;
      } catch (err) {
        setMutationError(
          err instanceof Error ? err.message : "Einstellungen konnten nicht gespeichert werden"
        );
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [refresh]
  );

  return {
    organization,
    isLoading,
    isSaving,
    error: mutationError || fetchError,
    saveSuccess,
    fetchSettings: refresh,
    updateSettings,
  };
}
